/**
 * Capacitor-compatible sharing utilities
 * Works on both web and native (iOS/Android via Capacitor)
 * 
 * For native apps: Install @capacitor/share
 * npm install @capacitor/share
 * npx cap sync
 */

// App store links (update these when you publish)
const APP_STORE_LINK = 'https://guapgoals.com'; // Replace with actual App Store/Play Store links
const IOS_APP_STORE = 'https://apps.apple.com/app/guapgoals/id123456789'; // Replace with actual ID
const ANDROID_PLAY_STORE = 'https://play.google.com/store/apps/details?id=com.guapgoals.app'; // Replace

/**
 * Check if running in Capacitor native environment
 */
export const isCapacitor = () => {
  return typeof window !== 'undefined' && 
         window.Capacitor !== undefined && 
         window.Capacitor.isNativePlatform();
};

/**
 * Check if Web Share API is available
 */
export const canShare = () => {
  return typeof navigator !== 'undefined' && navigator.share;
};

/**
 * Get the appropriate app download link based on platform
 */
export const getAppLink = () => {
  if (isCapacitor()) {
    // In native app, detect platform
    const platform = window.Capacitor.getPlatform();
    if (platform === 'ios') return IOS_APP_STORE;
    if (platform === 'android') return ANDROID_PLAY_STORE;
  }
  
  // For web, use universal link
  return APP_STORE_LINK;
};

/**
 * Share via native share sheet (Capacitor or Web Share API)
 * @param {Object} options - Share options
 * @param {string} options.title - Share title
 * @param {string} options.text - Share text/message
 * @param {string} options.url - URL to share (optional)
 * @returns {Promise<boolean>} - True if shared successfully
 */
export const shareNative = async ({ title, text, url }) => {
  // Try Capacitor Share plugin first (for native apps)
  if (isCapacitor()) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({
        title,
        text,
        url,
        dialogTitle: title
      });
      return true;
    } catch (error) {
      console.log('Capacitor Share not available, falling back to Web Share');
    }
  }
  
  // Try Web Share API (works on mobile browsers)
  if (canShare()) {
    try {
      await navigator.share({
        title,
        text: url ? `${text}\n${url}` : text,
        url
      });
      return true;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Web Share failed:', error);
      }
      return false;
    }
  }
  
  return false;
};

/**
 * Open SMS app with pre-filled message
 * Works on iOS, Android (both native and web)
 * @param {string} phoneNumber - Optional phone number (can be empty)
 * @param {string} message - Pre-filled message
 */
export const openSMS = (phoneNumber = '', message) => {
  const encodedMessage = encodeURIComponent(message);
  
  // Different formats for different platforms
  // iOS uses &body=, Android uses ?body=
  // Using ; separator works more universally
  const smsUrl = phoneNumber 
    ? `sms:${phoneNumber};?&body=${encodedMessage}`
    : `sms:?&body=${encodedMessage}`;
  
  window.open(smsUrl, '_blank');
};

/**
 * Open email client with pre-filled message
 * @param {string} email - Recipient email
 * @param {string} subject - Email subject
 * @param {string} body - Email body
 */
export const openEmail = (email, subject, body) => {
  const encodedSubject = encodeURIComponent(subject);
  const encodedBody = encodeURIComponent(body);
  
  const mailtoUrl = `mailto:${email}?subject=${encodedSubject}&body=${encodedBody}`;
  window.open(mailtoUrl, '_blank');
};

/**
 * Share household invitation
 * @param {string} householdName - Name of the household
 * @param {string} recipientEmail - Email of the person being invited (for email option)
 */
export const shareHouseholdInvite = async (householdName, recipientEmail = '') => {
  const appLink = getAppLink();
  const message = `Hey! I'm setting up our Household Budget "${householdName}" on GuapGoals. Download the app and let's get our money right! 💰`;
  
  return {
    message,
    appLink,
    
    // Try native share first
    shareNative: () => shareNative({
      title: `Join ${householdName} on GuapGoals`,
      text: message,
      url: appLink
    }),
    
    // Open SMS with pre-filled message
    sendText: () => openSMS('', `${message}\n\n${appLink}`),
    
    // Open email with pre-filled message
    sendEmail: () => openEmail(
      recipientEmail,
      `Join me on GuapGoals - ${householdName}`,
      `${message}\n\n${appLink}`
    )
  };
};

/**
 * Share envelope challenge invitation
 * @param {string} challengeName - Name of the challenge
 */
export const shareEnvelopeChallenge = async (challengeName) => {
  const appLink = getAppLink();
  const message = `I'm doing the ${challengeName} savings challenge on GuapGoals! Join me and let's stack some cash! 📨💰`;
  
  return {
    message,
    appLink,
    
    shareNative: () => shareNative({
      title: `Join my ${challengeName}`,
      text: message,
      url: appLink
    }),
    
    sendText: () => openSMS('', `${message}\n\n${appLink}`),
    
    sendEmail: (email = '') => openEmail(
      email,
      `Join my savings challenge on GuapGoals`,
      `${message}\n\n${appLink}`
    )
  };
};

/**
 * Share streak milestone
 * @param {number} streakDays - Number of streak days
 * @param {string} title - Streak title (e.g., "Paper Chaser")
 */
export const shareStreakMilestone = async (streakDays, title) => {
  const appLink = getAppLink();
  const message = `🔥 I just hit a ${streakDays}-day streak on GuapGoals! ${title ? `I'm now a "${title}" 💪` : ''} Get your money right too!`;
  
  return {
    message,
    appLink,
    
    shareNative: () => shareNative({
      title: 'My GuapGoals Streak',
      text: message,
      url: appLink
    }),
    
    sendText: () => openSMS('', `${message}\n\n${appLink}`),
    
    sendEmail: (email = '') => openEmail(
      email,
      'Check out my savings streak!',
      `${message}\n\n${appLink}`
    )
  };
};

export default {
  isCapacitor,
  canShare,
  getAppLink,
  shareNative,
  openSMS,
  openEmail,
  shareHouseholdInvite,
  shareEnvelopeChallenge,
  shareStreakMilestone
};
